/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  forwardRef,
  Injector,
  ChangeDetectorRef,
  input,
  output,
  effect,
  inject,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { SelectOption } from '../input.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-custom-multi-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CustomErrorDisplayComponent,
    TranslateModule,
    OverlayModule,
  ],
  templateUrl: './custom-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CustomMultiSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  label = input<string>('');
  name = input<string>('');
  options = input<SelectOption[]>([]);
  placeholder = input<string>('');
  errors = input<{ [key: string]: string }>({});
  displayError = input<boolean>(true);

  selectionChange = output<any[]>();

  private readonly _translate = inject(TranslateService);
  private readonly _injector = inject(Injector);
  private readonly _elementRef = inject(ElementRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  @ViewChild('dropdownTemplate') _dropdownTemplate!: TemplateRef<any>;
  private _overlayRef: OverlayRef | null = null;
  private _backdropClickSubscription: any;

  _value: any[] = [];
  isDisabled = false;
  isDropdownOpen = false;
  selectedOptionsMap: Map<any, boolean> = new Map();
  ngControl: NgControl | null = null;

  private readonly _uniqueId = `custom-multi-select-${Math.random().toString(36).substring(2, 9)}`;

  onChange: (value: any[]) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      // When options change, re-evaluate the selection map
      this.options();
      this._updateSelectionMapFromArray(this._value);
      this._cdr.markForCheck();
    });
  }

  get effectivePlaceholder(): string {
    const explicitPlaceholder = this.placeholder();
    if (
      explicitPlaceholder !== undefined &&
      explicitPlaceholder !== null &&
      explicitPlaceholder !== ''
    ) {
      return explicitPlaceholder;
    }
    return this._translate.instant('SHARED.INPUTS_CUSTOM-MULTI-SELECT.DEFAULT_PLACEHOLDER');
  }

  get effectiveName(): string {
    const currentName = this.name();
    if (currentName) return currentName;
    const currentLabel = this.label();
    if (currentLabel) {
      return currentLabel.toLowerCase().replace(/\s+/g, '-');
    }
    return this._uniqueId;
  }

  get isInvalid(): boolean {
    if (!this.ngControl?.control) {
      return false;
    }
    const { invalid, touched, dirty } = this.ngControl.control;
    return !!(invalid && (touched || dirty));
  }

  get displayText(): string {
    const selectedCount = this._value?.length || 0;
    if (selectedCount === 0) {
      return this.effectivePlaceholder;
    }
    if (selectedCount <= 2) {
      const currentOptions = this.options();
      const currentValue = this._value;
      const displayNames = currentOptions
        .filter((opt) => currentValue.includes(opt.value))
        .map((opt) => opt.display);
      return displayNames.join(', ');
    }
    return `${selectedCount} opciones seleccionadas`;
  }

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // El CDK overlay gestiona los clics fuera automáticamente

  writeValue(value: any[]): void {
    this._value = Array.isArray(value) ? value : [];
    this._updateSelectionMapFromArray(this._value);
    this._cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this._cdr.markForCheck();
  }

  private _updateSelectionMapFromArray(selectedValues: any[] | null): void {
    const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
    this.selectedOptionsMap.clear();
    this.options().forEach((option) => {
      this.selectedOptionsMap.set(option.value, currentValues.includes(option.value));
    });
  }

  toggleDropdown(): void {
    if (this.isDisabled) return;

    if (this.isDropdownOpen) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  }

  private _openDropdown(): void {
    if (this._overlayRef) {
      return;
    }

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._elementRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);

    const scrollStrategy = this._overlay.scrollStrategies.reposition();

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this._elementRef.nativeElement.getBoundingClientRect().width,
    });

    this._overlayRef = this._overlay.create(overlayConfig);
    const portal = new TemplatePortal(this._dropdownTemplate, this._viewContainerRef);
    this._overlayRef.attach(portal);

    this._backdropClickSubscription = this._overlayRef.backdropClick().subscribe(() => {
      this._closeDropdown();
      this.onTouched();
    });

    this.isDropdownOpen = true;
  }

  private _closeDropdown(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._backdropClickSubscription) {
      this._backdropClickSubscription.unsubscribe();
      this._backdropClickSubscription = null;
    }

    this.isDropdownOpen = false;
    this.onTouched();
  }

  onOptionChange(optionValue: any, event: Event): void {
    if (this.isDisabled) return;
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedOptionsMap.set(optionValue, isChecked);

    const newSelectedValues = this._getSelectedValuesFromMap();
    this._value = newSelectedValues;
    this.onChange(newSelectedValues);
    this.selectionChange.emit(newSelectedValues);
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._backdropClickSubscription) {
      this._backdropClickSubscription.unsubscribe();
      this._backdropClickSubscription = null;
    }
  }

  private _getSelectedValuesFromMap(): any[] {
    const selectedValues: any[] = [];
    this.selectedOptionsMap.forEach((isSelected, value) => {
      if (isSelected) {
        selectedValues.push(value);
      }
    });
    return selectedValues;
  }

  isOptionSelected(optionValue: any): boolean {
    return this.selectedOptionsMap.get(optionValue) || false;
  }
}
